import { defineComponent as _defineComponent } from 'vue'
import CompanyDepartmentsTable from '@/views/pages/companyDepartments/CompanyDepartmentsTable.vue';
import DepartmentCreateDialog from '@/views/pages/companyDepartments/DepartmentCreateDialog.vue';
import DepartmentCsvResultDialog from '@/views/pages/companyDepartments/DepartmentCsvResultDialog.vue';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { usePermissionComposable } from '@/composables/usePermission';
import { onMounted, ref } from 'vue';
import store from '@/store';
import router from '@/router';
import {
  DepartmentCsvRowError,
  DepartmentCsvUploadResult,
  DepartmentTreeNode,
} from '@/interfaces/model/general/department';
import { DepartmentCsvValidationError } from '@/store/main/actions/actionDepartment';
import { collectSubtreeIds } from '@/utils/departmentTree';
import sharedMessages from '@/lib/shared-messages';


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyDepartmentsPage',
  setup(__props) {

const showCreateDialog = ref(false);
const csvFileInput = ref<HTMLInputElement | null>(null);
const showCsvResultDialog = ref(false);
const csvResult = ref<DepartmentCsvUploadResult | null>(null);
const csvErrors = ref<DepartmentCsvRowError[]>([]);

const { hasPermission, PERMISSIONS: P } = usePermissionComposable();
const {
  isDownloadingCsv,
  isUploadingCsv,
  departmentTree,
  getCompanyDepartments,
  getCompanyDepartmentTree,
  downloadDepartmentsCsv,
  uploadDepartmentsCsv,
} = useDepartmentComposable();

/** ツリーと、他画面が参照するフラットな部署一覧の両方を更新する。 */
const refreshTree = async () => {
  await Promise.all([getCompanyDepartmentTree(), getCompanyDepartments()]);
};

/**
 * ツリーの件数は配下を含む集計なので、一覧へ渡す絞り込みも配下を含める。
 * そうしないとクリックした数字と遷移先の件数が食い違う。
 */
const goToUsersFilteredByDepartment = (node: DepartmentTreeNode) => {
  router.push({
    name: 'companyUsers',
    query: { department_uuids: collectSubtreeIds(node).join(',') },
  });
};

const goToSdsesFilteredByDepartment = (node: DepartmentTreeNode) => {
  router.push({
    name: 'sdsList',
    query: { department_uuids: collectSubtreeIds(node).join(',') },
  });
};

const handleDownloadCsv = async () => {
  try {
    await downloadDepartmentsCsv();
    store.commit('main/addNotification', {
      messageKey: sharedMessages.csvDownloadSuccess,
      color: 'success',
    });
  } catch (error) {
    console.error('CSV download failed:', error);
    store.commit('main/addNotification', {
      messageKey: sharedMessages.csvDownloadFailed,
      color: 'error',
    });
  }
};

const handleClickUploadCsv = () => {
  csvFileInput.value?.click();
};

const handleFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (!file) {
    return;
  }

  // Validate file type
  if (!file.name.endsWith('.csv')) {
    store.commit('main/addNotification', {
      messageKey: sharedMessages.csvFileRequired,
      color: 'error',
    });
    return;
  }

  try {
    csvErrors.value = [];
    csvResult.value = await uploadDepartmentsCsv(file);
    await refreshTree();
    // 移動は CSV に書かれていない子孫まで動かす。20 秒で消えるスナックバーでは
    // 前後のフルパスを読み切れないので、結果は必ずダイアログで見せる。
    showCsvResultDialog.value = true;
  } catch (error) {
    if (error instanceof DepartmentCsvValidationError) {
      // 検証エラー。DB には何も書かれていないので、ツリーは更新しない。
      csvResult.value = null;
      csvErrors.value = error.errors;
      showCsvResultDialog.value = true;
      return;
    }
    // それ以外 (認証・通信など) は共通ハンドラが通知済み
    console.error('CSV upload failed:', error);
  } finally {
    // Reset file input
    if (target) {
      target.value = '';
    }
  }
};

onMounted(async () => {
  await refreshTree();
});

return { __sfc: true,showCreateDialog, csvFileInput, showCsvResultDialog, csvResult, csvErrors, hasPermission, P, isDownloadingCsv, isUploadingCsv, departmentTree, getCompanyDepartments, getCompanyDepartmentTree, downloadDepartmentsCsv, uploadDepartmentsCsv, refreshTree, goToUsersFilteredByDepartment, goToSdsesFilteredByDepartment, handleDownloadCsv, handleClickUploadCsv, handleFileChange, CompanyDepartmentsTable, DepartmentCreateDialog, DepartmentCsvResultDialog, sharedMessages }
}

})